<template>
    <div v-if="show" class="editor-overlay" @click.self="close">
      <div class="editor-modal">
        <!-- Header -->
        <div class="editor-header">
          <h2>
            <i class="fas fa-edit"></i>
            Edit Resume - {{ resumeData?.metadata?.candidate_id }}
          </h2>
          <button @click="close" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
  
        <!-- Editor Content -->
        <div class="editor-content" v-if="editedData">
          
          <!-- Personal Information -->
          <section class="editor-section">
            <h3>Personal Information</h3>
            <div class="form-group">
              <label>Full Name</label>
              <input 
                v-model="editedData.personal_information.full_name" 
                type="text"
                class="form-input"
              >
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Email</label>
                <input 
                  v-model="editedData.personal_information.email" 
                  type="email"
                  class="form-input"
                >
              </div>
              <div class="form-group">
                <label>Phone</label>
                <input 
                  v-model="editedData.personal_information.phone" 
                  type="text"
                  class="form-input"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>City</label>
                <input 
                  v-model="editedData.personal_information.location.city" 
                  type="text"
                  class="form-input"
                >
              </div>
              <div class="form-group">
                <label>Country</label>
                <input 
                  v-model="editedData.personal_information.location.country" 
                  type="text"
                  class="form-input"
                >
              </div>
            </div>
          </section>
  
          <!-- Professional Summary -->
          <section class="editor-section">
            <h3>Professional Summary</h3>
            <textarea 
              v-model="editedData.professional_summary"
              class="form-textarea"
              rows="6"
            ></textarea>
          </section>
  
          <!-- Work Experience -->
          <section class="editor-section">
            <div class="section-header">
              <h3>Work Experience</h3>
              <button @click="addWorkExperience" class="btn-add">
                <i class="fas fa-plus"></i> Add Position
              </button>
            </div>
  
            <div 
              v-for="(job, index) in editedData.work_experience" 
              :key="index"
              class="work-item"
            >
              <div class="work-item-header">
                <span>Position {{ index + 1 }}</span>
                <button 
                  @click="removeWorkExperience(index)" 
                  class="btn-remove"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
  
              <!-- Job Title & Company -->
              <div class="form-row">
                <div class="form-group">
                  <label>Job Title *</label>
                  <input 
                    v-model="job.job_title" 
                    type="text"
                    class="form-input"
                    placeholder="Engineering Manager"
                  >
                </div>
                <div class="form-group">
                  <label>Company *</label>
                  <input 
                    v-model="job.company_name" 
                    type="text"
                    class="form-input"
                    placeholder="Company Name"
                  >
                </div>
              </div>
  
              <!-- Location & Seniority -->
              <div class="form-row">
                <div class="form-group">
                  <label>Location</label>
                  <input 
                    v-model="job.location" 
                    type="text"
                    class="form-input"
                    placeholder="Paris, France"
                  >
                </div>
                <div class="form-group">
                  <label>Seniority</label>
                  <select v-model="job.seniority" class="form-input">
                    <option :value="null">Not specified</option>
                    <option value="junior">Junior</option>
                    <option value="confirme">Confirmé</option>
                    <option value="senior">Senior</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
              </div>
  
              <!-- Contract Type & Work Mode -->
              <div class="form-row">
                <div class="form-group">
                  <label>Contract Type</label>
                  <select v-model="job.contract_type" class="form-input">
                    <option :value="null">Not specified</option>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="freelance">Freelance</option>
                    <option value="stage">Stage</option>
                    <option value="alternance">Alternance</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Work Mode</label>
                  <select v-model="job.work_mode" class="form-input">
                    <option :value="null">Not specified</option>
                    <option value="presentiel">Présentiel</option>
                    <option value="teletravail">Télétravail</option>
                    <option value="hybride">Hybride</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>
              </div>
  
              <!-- Dates -->
              <div class="form-row">
                <div class="form-group">
                  <label>Start Date *</label>
                  <input 
                    v-model="job.employment_dates.start" 
                    type="text"
                    class="form-input"
                    placeholder="YYYY-MM-DD"
                  >
                </div>
                <div class="form-group">
                  <label>End Date</label>
                  <input 
                    v-model="job.employment_dates.end" 
                    type="text"
                    class="form-input"
                    placeholder="YYYY-MM-DD or Present"
                  >
                </div>
              </div>
  
              <!-- Responsibilities -->
              <div class="form-group">
                <label>Responsibilities & Achievements</label>
                <div 
                  v-for="(resp, respIndex) in job.responsibilities_and_achievements" 
                  :key="respIndex"
                  class="responsibility-item"
                >
                  <textarea
                    v-model="job.responsibilities_and_achievements[respIndex]"
                    class="form-textarea-small"
                    rows="2"
                  ></textarea>
                  <button 
                    @click="removeResponsibility(job, respIndex)"
                    class="btn-remove-inline"
                    title="Remove"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <button 
                  @click="addResponsibility(job)" 
                  class="btn-add-small"
                >
                  <i class="fas fa-plus"></i> Add Responsibility
                </button>
              </div>
            </div>
          </section>
  
          <!-- Education -->
          <section class="editor-section">
            <div class="section-header">
              <h3>Education</h3>
              <button @click="addEducation" class="btn-add">
                <i class="fas fa-plus"></i> Add Education
              </button>
            </div>
  
            <div 
              v-for="(edu, index) in editedData.education" 
              :key="index"
              class="work-item"
            >
              <div class="work-item-header">
                <span>Education {{ index + 1 }}</span>
                <button 
                  @click="removeEducation(index)" 
                  class="btn-remove"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
  
              <div class="form-row">
                <div class="form-group">
                  <label>Degree</label>
                  <input 
                    v-model="edu.degree" 
                    type="text"
                    class="form-input"
                    placeholder="M.S., B.S., etc."
                  >
                </div>
                <div class="form-group">
                  <label>Major/Field</label>
                  <input 
                    v-model="edu.major" 
                    type="text"
                    class="form-input"
                    placeholder="Computer Science"
                  >
                </div>
              </div>
  
              <div class="form-row">
                <div class="form-group">
                  <label>Institution</label>
                  <input 
                    v-model="edu.institution" 
                    type="text"
                    class="form-input"
                    placeholder="University Name"
                  >
                </div>
                <div class="form-group">
                  <label>Graduation Date</label>
                  <input 
                    v-model="edu.graduation_date" 
                    type="text"
                    class="form-input"
                    placeholder="YYYY-MM-DD"
                  >
                </div>
              </div>
            </div>
          </section>
  
          <!-- Skills -->
          <section class="editor-section">
            <h3>Skills</h3>
            
            <div class="form-group">
              <label>Technical Skills (comma-separated)</label>
              <textarea 
                v-model="skillsText.technical"
                class="form-textarea"
                rows="3"
                placeholder="Python, JavaScript, Docker, AWS..."
              ></textarea>
            </div>
  
            <div class="form-group">
              <label>Languages (comma-separated)</label>
              <textarea 
                v-model="skillsText.languages"
                class="form-textarea"
                rows="2"
                placeholder="English C2, French B2, Spanish A1..."
              ></textarea>
            </div>
          </section>
  
          <!-- Certifications -->
          <section class="editor-section">
            <div class="section-header">
              <h3>Certifications</h3>
              <button @click="addCertification" class="btn-add">
                <i class="fas fa-plus"></i> Add Certification
              </button>
            </div>
  
            <div 
              v-for="(cert, index) in editedData.certifications" 
              :key="index"
              class="cert-item"
            >
              <input 
                v-model="editedData.certifications[index]"
                type="text"
                class="form-input"
                placeholder="Certification name and date"
              >
              <button 
                @click="removeCertification(index)"
                class="btn-remove-inline"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </section>
  
        </div>
  
        <!-- Footer Actions -->
        <div class="editor-footer">
          <div class="footer-left">
            <label class="checkbox-label">
              <input type="checkbox" v-model="saveToES">
              <span>Save changes to Elasticsearch</span>
            </label>
          </div>
          <div class="footer-right">
            <button @click="close" class="btn-secondary">
              Cancel
            </button>
            <button @click="saveChanges" class="btn-primary" :disabled="isSaving">
              <i :class="isSaving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
              {{ isSaving ? 'Saving...' : 'Save & Export' }}
            </button>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  
  export default {
    name: 'ResumeEditor',
    props: {
      show: {
        type: Boolean,
        required: true
      },
      resumeData: {
        type: Object,
        required: false,
        default: null
      }
    },
    
    data() {
      return {
        editedData: null,
        skillsText: {
          technical: '',
          languages: ''
        },
        saveToES: true, // Default to saving changes
        isSaving: false
      };
    },
    
    watch: {
      show(newVal) {
        if (newVal && this.resumeData) {
          this.initializeEditor();
        }
      }
    },
    
    methods: {
      initializeEditor() {
        // Deep clone the resume data
        this.editedData = JSON.parse(JSON.stringify(this.resumeData));
        
        // Initialize personal_information structure
        if (!this.editedData.personal_information) {
          this.editedData.personal_information = {
            full_name: '',
            email: '',
            phone: '',
            location: { city: '', country: '' },
            linkedin: [],
            github: []
          };
        } else {
          // Ensure location object exists
          if (!this.editedData.personal_information.location) {
            this.editedData.personal_information.location = { city: '', country: '' };
          }
          // Convert arrays to strings for easier editing
          if (Array.isArray(this.editedData.personal_information.email)) {
            this.editedData.personal_information.email = this.editedData.personal_information.email[0] || '';
          }
          if (Array.isArray(this.editedData.personal_information.phone)) {
            this.editedData.personal_information.phone = this.editedData.personal_information.phone[0] || '';
          }
        }
        
        // Initialize work_experience
        if (!this.editedData.work_experience) {
          this.editedData.work_experience = [];
        } else {
          // Ensure each job has proper structure
          this.editedData.work_experience = this.editedData.work_experience.map(job => ({
            job_title: job.job_title || '',
            company_name: job.company_name || job.company || '',
            location: job.location || null,
            seniority: job.seniority || null,
            contract_type: job.contract_type || null,
            work_mode: job.work_mode || null,
            employment_dates: {
              start: job.employment_dates?.start || job.start_date || '',
              end: job.employment_dates?.end || job.end_date || '',
              employment_period: job.employment_dates?.employment_period || null
            },
            responsibilities_and_achievements: job.responsibilities_and_achievements || job.responsibilities || ['']
          }));
        }
        
        // Initialize education
        if (!this.editedData.education) {
          this.editedData.education = [];
        }
        
        // Initialize skills
        if (!this.editedData.skills) {
          this.editedData.skills = { technical: [], inferred_skills: [], languages: [] };
        }
        
        // Convert skills arrays to comma-separated strings
        this.skillsText.technical = (this.editedData.skills.technical || []).join(', ');
        this.skillsText.languages = (this.editedData.skills.languages || []).join(', ');
        
        // Initialize certifications
        if (!this.editedData.certifications) {
          this.editedData.certifications = [];
        }
        
        // Initialize projects
        if (!this.editedData.projects) {
          this.editedData.projects = [];
        }
      },
      
      // Work Experience Methods
      addWorkExperience() {
        this.editedData.work_experience.push({
          job_title: '',
          company_name: '',
          location: null,
          seniority: null,
          contract_type: null,
          work_mode: null,
          employment_dates: {
            start: '',
            end: '',
            employment_period: null
          },
          responsibilities_and_achievements: ['']
        });
      },
      
      removeWorkExperience(index) {
        if (confirm('Remove this position?')) {
          this.editedData.work_experience.splice(index, 1);
        }
      },
      
      addResponsibility(job) {
        if (!job.responsibilities_and_achievements) {
          job.responsibilities_and_achievements = [];
        }
        job.responsibilities_and_achievements.push('');
      },
      
      removeResponsibility(job, index) {
        job.responsibilities_and_achievements.splice(index, 1);
      },
      
      // Education Methods
      addEducation() {
        this.editedData.education.push({
          degree: '',
          major: null,
          institution: '',
          location: null,
          graduation_date: ''
        });
      },
      
      removeEducation(index) {
        if (confirm('Remove this education?')) {
          this.editedData.education.splice(index, 1);
        }
      },
      
      // Certification Methods
      addCertification() {
        this.editedData.certifications.push('');
      },
      
      removeCertification(index) {
        this.editedData.certifications.splice(index, 1);
      },
      
      // Save Methods
      async saveChanges() {
        this.isSaving = true;
        
        try {
          // Convert skills text back to arrays
          this.editedData.skills.technical = this.skillsText.technical
            .split(',')
            .map(s => s.trim())
            .filter(s => s);
          
          this.editedData.skills.languages = this.skillsText.languages
            .split(',')
            .map(s => s.trim())
            .filter(s => s);
          
          // Convert email/phone back to arrays for ES
          const dataToSave = JSON.parse(JSON.stringify(this.editedData));
          dataToSave.personal_information.email = [dataToSave.personal_information.email];
          dataToSave.personal_information.phone = [dataToSave.personal_information.phone];
          
          const candidateId = this.editedData.metadata?.candidate_id;
          
          if (!candidateId) {
            throw new Error('No candidate ID found');
          }
          
          // Save to ES if checkbox is checked
          if (this.saveToES) {
            await this.updateElasticsearch(candidateId, dataToSave);
            this.$emit('save-complete', 'Resume updated successfully in Elasticsearch!');
          } else {
            this.$emit('save-complete', 'Changes saved locally (not persisted to database)');
          }
          
          this.close();
          
        } catch (error) {
          console.error('Save failed:', error);
          this.$emit('error', error.message);
        } finally {
          this.isSaving = false;
        }
      },
      
      async updateElasticsearch(candidateId, data) {
        const apiBaseUrl = this.getApiBaseUrl();
        
        // const response = await axios.patch(
        //   `${apiBaseUrl}/api/v1/resume/${candidateId}`,
        //   data
        // );
        const response = await axios.patch( `/api/v1/resume/${candidateId}`,data);
        
        if (!response.data.success) {
          throw new Error('Failed to update Elasticsearch');
        }
        
        console.log('✅ Elasticsearch updated successfully');
      },
      
      getApiBaseUrl() {
        if (process.env.NODE_ENV === 'development') {
          return process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000';
        }
        return '';
      },
      
      close() {
        this.$emit('close');
      }
    }
  };
  </script>
  
  <style scoped>
  .editor-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
  
  .editor-modal {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  .editor-header {
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .editor-header h2 {
    margin: 0;
    color: #333;
    font-size: 20px;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
  }
  
  .close-btn:hover {
    color: #B21C28;
  }
  
  .editor-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
  }
  
  .editor-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .editor-section:last-child {
    border-bottom: none;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }
  
  .editor-section h3 {
    color: #B21C28;
    font-size: 18px;
    margin-bottom: 15px;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 5px;
    color: #555;
    font-weight: 500;
    font-size: 14px;
  }
  
  .form-input,
  .form-textarea,
  .form-textarea-small {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
  }
  
  .form-textarea-small {
    resize: vertical;
    min-height: 60px;
  }
  
  .form-input:focus,
  .form-textarea:focus,
  .form-textarea-small:focus {
    outline: none;
    border-color: #B21C28;
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  
  .work-item {
    background: #f8f8f8;
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 15px;
  }
  
  .work-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    font-weight: 600;
    color: #666;
  }
  
  .responsibility-item {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    align-items: flex-start;
  }
  
  .cert-item {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
  }
  
  .btn-add,
  .btn-add-small {
    background: #B21C28;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  
  .btn-add-small {
    padding: 6px 12px;
    font-size: 13px;
    margin-top: 10px;
  }
  
  .btn-add:hover,
  .btn-add-small:hover {
    background: #8f1620;
  }
  
  .btn-remove,
  .btn-remove-inline {
    background: #dc3545;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  .btn-remove-inline {
    padding: 4px 8px;
  }
  
  .btn-remove:hover,
  .btn-remove-inline:hover {
    background: #c82333;
  }
  
  .editor-footer {
    padding: 20px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .footer-left {
    display: flex;
    align-items: center;
  }
  
  .checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
  }
  
  .checkbox-label input {
    margin-right: 8px;
  }
  
  .footer-right {
    display: flex;
    gap: 10px;
  }
  
  .btn-secondary,
  .btn-primary {
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    border: none;
  }
  
  .btn-secondary {
    background: #f0f0f0;
    color: #333;
  }
  
  .btn-secondary:hover {
    background: #e0e0e0;
  }
  
  .btn-primary {
    background: #B21C28;
    color: white;
  }
  
  .btn-primary:hover:not(:disabled) {
    background: #8f1620;
  }
  
  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  </style>